import type { ReactNode } from 'react';
import { Link, useRouter } from '../../router';
import { Field, Switch } from '../../ui/kit';
import { useDraft } from '../draft';
import { getPath, type ConfigGroup } from '../model';
import { settingsTabPath } from '../tabs';
import { issuesFor } from '../validate';

/** Read/write one dotted path of the draft, with its issues. */
export function usePath<T>(path: string): { value: T; set: (v: T) => void; issues: ReturnType<typeof issuesFor> } {
  const draft = useDraft();
  return {
    value: getPath(draft.edited, path) as T,
    set: (v: T) => draft.update(path, v),
    issues: issuesFor(draft.issues, path),
  };
}

/**
 * A text field where EMPTY means `null` — "keep the widget's built-in copy",
 * never a blank string (the server treats "" as invalid and normalises it).
 * `placeholder` should be that built-in copy itself (from the widget's
 * defaultCopy.ts), so the partner sees what an empty field shows.
 */
export function NullableText({
  path,
  label,
  hint,
  multiline,
  placeholder,
  maxLength,
}: {
  path: string;
  label: ReactNode;
  hint?: ReactNode;
  multiline?: boolean;
  placeholder?: string;
  maxLength?: number;
}) {
  const { value, set, issues } = usePath<string | null>(path);
  return (
    <Field label={label} hint={hint} issues={issues} optional>
      {(p) =>
        multiline ? (
          <textarea
            {...p}
            className="input"
            value={value ?? ''}
            placeholder={placeholder}
            maxLength={maxLength}
            onChange={(e) => set(e.target.value.trim() === '' ? null : e.target.value)}
          />
        ) : (
          <input
            {...p}
            className="input"
            value={value ?? ''}
            placeholder={placeholder}
            maxLength={maxLength}
            onChange={(e) => set(e.target.value.trim() === '' ? null : e.target.value)}
          />
        )
      }
    </Field>
  );
}

export function PathSwitch({ path, label, description, disabled }: { path: string; label: ReactNode; description?: ReactNode; disabled?: boolean }) {
  const { value, set } = usePath<boolean>(path);
  return <Switch label={label} description={description} checked={Boolean(value)} onChange={set} disabled={disabled} />;
}

export function PathSelect<T extends string>({
  path,
  label,
  hint,
  options,
}: {
  path: string;
  label: ReactNode;
  hint?: ReactNode;
  options: Array<{ value: T; label: string }>;
}) {
  const { value, set, issues } = usePath<T>(path);
  return (
    <Field label={label} hint={hint} issues={issues}>
      {(p) => (
        <select {...p} className="input" value={value} onChange={(e) => set(e.target.value as T)}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

export function SectionCard({ title, description, children, id }: { title: string; description?: ReactNode; children: ReactNode; id?: string }) {
  return (
    <section className="panel" aria-labelledby={id ? `${id}-h` : undefined}>
      <div className="panel-head">
        <div className="stack-sm" style={{ gap: 2 }}>
          <h2 id={id ? `${id}-h` : undefined}>{title}</h2>
          {description ? <p className="small muted">{description}</p> : null}
        </div>
      </div>
      <div className="panel-body stack">{children}</div>
    </section>
  );
}

/**
 * "Formatting: **bold**, [links](…)" — the hint for every field the widget
 * renders with its inline formatter, instead of the word "Markdown".
 */
export const FORMATTING_HINT = 'Formatting: **bold**, [links](https://…).';

/** A link to one tab of Widget settings (`/config?tab=…`). */
export function SettingsTabLink({ tab, children }: { tab: ConfigGroup; children: ReactNode }) {
  const { path } = useRouter();
  const onConfig = path === '/config';
  return <Link to={settingsTabPath(tab)}>{onConfig ? children : <>Widget settings → {children}</>}</Link>;
}

/**
 * The FinderlyFix app handoff, as the widget decides it (App.tsx
 * `showAppCta`, routing.ts `resolveHandoffActions`):
 *   - `handoff.app_enabled` is the master switch: off, no app button anywhere.
 *   - `copy.cta_app` is the button's label; `null` keeps it off ordinary
 *     results, and it then shows only for problems outside the partner's
 *     trades (scope "Diagnose, then offer the FinderlyFix app").
 * Both are edited on the App handoff tab only; everything else reads this.
 */
export function useAppHandoff(): { enabled: boolean; label: string | null; where: 'off' | 'every' | 'out_of_scope' } {
  const { edited } = useDraft();
  const enabled = edited.handoff.app_enabled;
  const label = edited.copy.cta_app;
  return { enabled, label, where: !enabled ? 'off' : label !== null ? 'every' : 'out_of_scope' };
}

/** The app handoff's state, read-only, with a link to the one place it is edited. */
export function AppHandoffStatus({ context }: { context?: string }) {
  const app = useAppHandoff();
  const state =
    app.where === 'off'
      ? 'Off: visitors are never offered the FinderlyFix app.'
      : app.where === 'every'
        ? `On: visitors see a “${app.label}” button after a diagnosis.`
        : 'On only for problems outside your trades.';
  return (
    <div className="readonly-setting" data-testid="app-handoff-status">
      <span className="small">
        <strong>App handoff button</strong> · {state}
        {context ? <> {context}</> : null}
      </span>
      <span className="small">
        <SettingsTabLink tab="handoff">Change in App handoff</SettingsTabLink>
      </span>
    </div>
  );
}

/** A collapsed "Advanced (developers)" group for settings that need code on the partner's side. */
export function AdvancedDisclosure({ children, open }: { children: ReactNode; open?: boolean }) {
  return (
    <details className="advanced" open={open}>
      <summary>Advanced (developers)</summary>
      <div className="stack advanced-body">{children}</div>
    </details>
  );
}
